import { useOutletContext, useSearchParams, Link } from 'react-router-dom';
import { FormControl, Paper, Alert } from '@mui/material';
import dayjs from "dayjs";

import StatisticsTable from "~/tables/StatisticsTable";
import Page from '~/components/Page';
import { processConsumpitonsDays } from "~/providers/consumption";
import { formatDate, formatNumberToThousands } from "~/utils/utils";
import { useTrans } from "~/hooks/useTrans";
import { getProjectBreadcrumb } from "~/utils/breadcrumbs";
import TableControls from '~/components/TableControls';
import { MySelect, MyMenuItem } from '~/mui/MySelect';
import { apiGetKeyConsumption, apiGetProject, apiGetProjectConsumption, apiGetProjectKeys } from "~/providers/api";
import { IClientKey, IClientProject } from "~/providers/client-interfaces";
import { IConsumption } from "~/providers/interfaces";
import { IOutlet, IPage } from "~/interfaces";
import Loader from '~/components/Loader';
import ErrorInfo from '~/components/ErrorInfo';
import { IAdminKey, IAdminProject } from "~/providers/admin-interfaces";
import { myUseState } from "~/hooks/myUseState";
import { getLink } from "~/providers/route";
import { useLang } from "~/hooks/useLang";
import BarGraph, { IBarGraphItem } from "~/components/Graph";

import "./style.less";

interface IDateItem {
	allCount: number;
	services: {
		[key: string]: number;
	}
}

interface IDatesObj {
	[key: string]: IDateItem;
}

interface IState {
	loading: boolean;
	errorMsg: string;
	project: IClientProject | IAdminProject;
	consumptions: Array<IConsumption>;
	filterValue: string;
	filterItems: Array<{
		value: string;
		title: string;
	}>;
	graphColumns: Array<string>;
	graphItems: Array<IBarGraphItem>;
}

const GRAPH_HEIGHT = 250;

export default function ProjectConsumptionDetailPage({
	isInternal = false,
}: IPage) {
	const { trans } = useTrans();
	const { lang, isCzech } = useLang();
	const ALL_FILTER_ITEM = {
		value: "",
		title: trans("consumptionPage.sort.allKeys"),
	};
	const INIT_STATE: IState = {
		loading: true,
		errorMsg: "",
		project: null,
		consumptions: [],
		filterValue: "",
		filterItems: [ALL_FILTER_ITEM],
		graphItems: [],
		graphColumns: [],
	};
	const { state, updateState } = myUseState<IState>(INIT_STATE);
	const { params }: IOutlet = useOutletContext();
	const [searchParams] = useSearchParams();
	const fromDate = searchParams.get("fromDate") || "";
	const tillDate = searchParams.get("tillDate") || "";
	const title = trans("projectConsumptionHistoryDetailPage.title", {
		range: `${formatDate(new Date(fromDate), isCzech)} - ${formatDate(new Date(tillDate), isCzech)}`,
	});

	function getKeyTitle(keyData: IClientKey | IAdminKey): string {
		let titlePart = "";

		if (keyData.userTitle) {
			titlePart = `${keyData.userTitle} (${keyData.internalName})`;
		} else {
			titlePart = keyData.internalName;
		}

		return `${trans("apiKey")} ${titlePart}`;
	}

	function getConsumption(keyId = 0) {
		if (keyId === 0) {
			return apiGetProjectConsumption(isInternal ? "admin" : "client", params.projectId, fromDate, tillDate, lang);
		}

		return apiGetKeyConsumption(isInternal ? "admin" : "client", params.projectId, keyId, fromDate, tillDate, lang);
	}

	function createGraphData(consumptions: Array<IConsumption>) {
		const graphItems: Array<IBarGraphItem> = [];
		let graphColumns = [];

		if (consumptions.length > 0) {
			const datesObj: IDatesObj = {};
			const allServices: Array<string> = [];

			for (const item of consumptions) {
				if (!(item.date in datesObj)) {
					datesObj[item.date] = {
						allCount: 0,
						services: {},
					};
				}

				if (!allServices.includes(item.service)) {
					allServices.push(item.service);
				}

				datesObj[item.date].allCount += item.count;
				datesObj[item.date].services[item.service] = item.count;
			}

			allServices.sort((aItem, bItem) => aItem.localeCompare(bItem));

			graphColumns = [];

			// doplnime nuly
			const allCountName = "count";
			const allDates = Object.keys(datesObj);
			const dataMapping: {[key: string]: Array<number>} = {};

			allDates.sort((aItem, bItem) => aItem.localeCompare(bItem));

			for (const date of allDates) {
				const dateData: IDateItem = datesObj[date];

				if (!(allCountName in dataMapping)) {
					dataMapping[allCountName] = [];
				}

				dataMapping[allCountName].push(dateData.allCount);

				for (const service of allServices) {
					if (!(service in dataMapping)) {
						dataMapping[service] = [];
					}

					dataMapping[service].push(dateData.services[service]);
				}

				graphColumns.push(dayjs(date).format("DD. M. YYYY"));
			}

			graphItems.push({
				label: trans("amountTotal"),
				values: dataMapping[allCountName],
			});

			for (const service of allServices) {
				graphItems.push({
					label: service,
					values: dataMapping[service],
				});
			}
		}

		return {
			graphItems,
			graphColumns,
		};
	}

	async function changeMainFilter(filterValue: string) {
		updateState({
			filterValue,
			consumptions: [],
			graphItems: [],
			graphColumns: [],
			loading: true,
		});

		const keyId = filterValue ? parseFloat(filterValue) : 0;
		const consumptionData = await getConsumption(keyId);

		updateState({
			loading: false,
			...createGraphData(consumptionData.noErrors ? consumptionData.data : []),
			...consumptionData.noErrors
				? {
					consumptions: consumptionData.data,
				}
				: {},
			errorMsg: consumptionData.errors.join(", "),
		});
	}

	async function loadData() {
		updateState(INIT_STATE);

		const [projectData, keysData, consumptionData] = await Promise.all([
			apiGetProject(isInternal ? "admin" : "client", params.projectId, lang),
			apiGetProjectKeys(isInternal ? "admin" : "client", params.projectId, lang),
			getConsumption(),
		]);

		updateState({
			loading: false,
			...projectData.noErrors
				? {
					project: projectData.data,
				}
				: {},
			...keysData.noErrors
				? {
					filterItems: [
						ALL_FILTER_ITEM,
						...keysData.data.map((key: IClientKey | IAdminKey) => ({
							value: key.id.toString(),
							title: getKeyTitle(key),
						})),
					],
				}
				: {},
			...createGraphData(consumptionData.noErrors ? consumptionData.data : []),
			...consumptionData.noErrors
				? {
					consumptions: consumptionData.data,
				}
				: {},
			errorMsg: [
				...projectData.errors,
				...keysData.errors,
				...consumptionData.errors,
			].join(", "),
		});
	}

	function onPageLoad(isAuthorized: boolean) {
		if (isAuthorized) {
			loadData();
		}
	}

	function getYAxe(value: string) {
		return `${formatNumberToThousands(parseFloat(value))}`;
	}

	return <Page checkLogged={true} title={title} breadcrumbs={getProjectBreadcrumb(trans, isInternal, state.project, [trans("menu.consumption"), title])} checkInternalRole={isInternal ? "salesman" : "none"}
		onLoad={onPageLoad} seoTitleTrans="seo.title.consumption.detail">
		<Paper elevation={3} className="projectConsumptionDetailPage__paper">
			<TableControls className="projectConsumptionDetailPage__tableControls">
				<FormControl size="small">
					<MySelect value={state.filterValue} onChange={event => changeMainFilter(event.target.value as string)}>
						{ state.filterItems.map(item => <MyMenuItem value={item.value} key={item.value}>
							{ item.title }
						</MyMenuItem>) }
					</MySelect>
				</FormControl>
				<Link to={getLink("project-consumption-history", isInternal, { projectId: params.projectId })}>
					{ trans("projectConsumptionHistoryDetailPage.goBack") }
				</Link>
			</TableControls>
			{ state.graphItems.length > 0 && <div className="projectConsumptionDetailPage__grafHolder">
				<BarGraph items={state.graphItems} columns={state.graphColumns} getYAxe={getYAxe} height={GRAPH_HEIGHT} />
			</div> }
			{ state.loading && <Loader msg={trans("loadingData")} /> }
			{ !state.loading && <>
				<StatisticsTable data={processConsumpitonsDays(trans("day"), state.consumptions, isCzech)} />
				<Alert severity="info" className="margin-top-small">
					{ trans("consumptionPage.info1") }<br />
					{ trans("consumptionPage.info2") }<br />
				</Alert>
			</> }
		</Paper>
		{ state.errorMsg && <ErrorInfo msg={state.errorMsg} className="margin-top-large" /> }
	</Page>;
}
